// The two enrolment requests, and their own reading of what came back.
//
// WHY THIS DOES NOT USE `failureFor`, WHICH IS THE WHOLE REASON THIS FILE EXISTS.
// client.ts says its mapper is correct only for a request carrying a session and
// that the login routes need their own; the enrolment confirmation is a third
// case, and the sharpest one. It answers 401 for two unrelated things:
//
//   401 error=unauthorised        — the session is dead. Forget the token.
//   401 error=second_factor_wrong — the session is fine and six digits are not.
//
// Run through `failureFor`, the second becomes `session_ended`, and a person
// mistyping one digit of an authenticator code is signed out and has to start
// from the emailed code again. They would then enrol, mistype, be signed out,
// and sign in without a factor — which is the dead end this screen exists to
// end, rebuilt out of a status code. So the discrimination here is on the
// `error` field and not on the status, and it is the one place in this console
// that reads that field for control flow rather than for display.
//
// 404 IS ALSO TWO THINGS, and the same way. routes.ts registers the enrolment
// pair only when a sealing key is configured — "a route that answers 501 to
// everybody is a route somebody eventually fixes by making it work without the
// key" — so on an instance with no key the routes are absent and Fastify answers
// its own 404, with no `error` field of ours. Meanwhile the confirmation's own
// 404 means the pending enrolment expired or was superseded. One says "this
// service cannot do this at all", the other says "press begin again". Reading
// only the status would show a person a retry button for a server that has no
// key, forever.
//
// NOTHING HERE DECIDES WHETHER A WRONG CODE WAS WRONG OR REPLAYED, and that is
// not an omission. api/src/auth/login.ts gives one answer for "those digits are
// wrong", "those digits were already spent in this thirty seconds" and "this
// server cannot open the secret", because separating the second "would tell
// somebody replaying a code they had observed that they had the right digits and
// merely the wrong moment". The confirmation route is narrower still: it does not
// go through verifyAndClaim, a pending factor has no spent step to collide with,
// and so `second_factor_wrong` is the only code refusal it can produce. A console
// that showed "that code was already used" would be inventing a distinction the
// server refused to make, on this path could not make, and would be wrong about.
// What the server's own sentence does carry is the action for both causes — wait
// for the next code, and check the phone's clock — so it is passed through whole.

import { AUTH_ENDPOINTS } from "./endpoints.js";
import { authenticated, type Answer, type ApiOptions } from "./client.js";
import type { SessionToken } from "../session/store.js";

/**
 * What `begin` handed back, once.
 *
 * `secret` and `otpauthUri` are the same bytes in two encodings and there is no
 * route that reads either of them again. They live in React state for as long as
 * this screen is open and are not put anywhere that outlives it — see Enrol.tsx.
 */
export type Enrolment = {
  readonly factorId: string;
  /** base32, unpadded. What somebody types into an app that cannot scan. */
  readonly secret: string;
  /** The provisioning URI. What the QR symbol encodes. */
  readonly otpauthUri: string;
  /** ISO 8601. The pending row is good until this and then it is not. */
  readonly expiresAt: string;
  readonly algorithm: string;
  readonly digits: number;
  readonly periodSeconds: number;
  /** The server's own sentence about what has and has not happened yet. */
  readonly detail: string;
};

export type Confirmation = {
  readonly factorId: string;
  readonly confirmedAt: string;
  /**
   * The server's sentence, and on this response it is load-bearing.
   *
   * It is the one that says the session in this browser was opened before the
   * factor existed, is unchanged, and will still be refused. Enrol.tsx draws it
   * rather than a congratulation.
   */
  readonly detail: string;
};

export type Refusal =
  /** The session is not live. The caller clears it, exactly as a report 401 does. */
  | { readonly kind: "session_ended"; readonly detail: string }
  /**
   * The code did not confirm the enrolment. The session is untouched and the
   * person retypes. Distinct from `session_ended` despite sharing its status.
   */
  | { readonly kind: "code_refused"; readonly detail: string }
  /** The pending enrolment expired or was superseded. Begin again. */
  | { readonly kind: "nothing_to_confirm"; readonly detail: string }
  /**
   * There is already a confirmed factor and this session did not prove it.
   *
   * Its own outcome because it is the only refusal on this screen that no button
   * here can resolve: the ways out are signing in with the existing authenticator
   * or asking Nova to reset it, and both are somewhere else.
   */
  | { readonly kind: "already_enrolled"; readonly detail: string }
  /**
   * This service does not offer enrolment — no sealing key, or a key that cannot
   * open what it issued. Not the person's fault and not retryable by them.
   */
  | { readonly kind: "not_offered"; readonly detail: string }
  | { readonly kind: "unavailable"; readonly detail: string }
  | { readonly kind: "malformed"; readonly detail: string }
  | { readonly kind: "unreachable"; readonly detail: string };

export type Attempt<T> = { readonly ok: true; readonly value: T } | ({ readonly ok: false } & Refusal);

/** Begin an enrolment. Every call supersedes any pending one and yields a new secret. */
export async function beginEnrolment(options: ApiOptions, token: SessionToken): Promise<Attempt<Enrolment>> {
  const answer = await authenticated(options, token, {
    path: AUTH_ENDPOINTS.beginSecondFactor,
    method: "POST",
  });

  if (!answer.ok) return { ok: false, ...refusalFor(answer) };

  const enrolment = asEnrolment(answer.body);
  if (enrolment === null) {
    // Refused rather than half-drawn. A missing `otpauth_uri` with a present
    // `secret` would otherwise render a QR symbol built from the string
    // "undefined": it would scan, and it would enrol somebody's authenticator
    // against nothing, and they would find out at the next sign-in.
    return {
      ok: false,
      kind: "unreachable",
      detail:
        "The service answered the enrolment request with something that is not an enrolment. Nothing has " +
        "been shown, because a partly-read enrolment would produce a code that scans and matches nothing.",
    };
  }

  return { ok: true, value: enrolment };
}

/** Confirm the pending enrolment with a code from the authenticator. */
export async function confirmEnrolment(
  options: ApiOptions,
  token: SessionToken,
  code: string,
): Promise<Attempt<Confirmation>> {
  const answer = await authenticated(options, token, {
    path: AUTH_ENDPOINTS.confirmSecondFactor,
    method: "POST",
    json: { code },
  });

  if (!answer.ok) return { ok: false, ...refusalFor(answer) };

  const confirmation = asConfirmation(answer.body);
  if (confirmation === null) {
    return {
      ok: false,
      kind: "unreachable",
      detail:
        "The service accepted that code and did not say what it had done. Do not assume the factor is in " +
        "force: sign in again, and if the authenticator is not asked for, begin enrolment again.",
    };
  }

  return { ok: true, value: confirmation };
}

/**
 * What a refusal from either enrolment route means.
 *
 * Reads `error` before `status` wherever the two disagree, per the header.
 */
export function refusalFor(answer: Extract<Answer, { ok: false }>): Refusal {
  const error = typeof answer.body?.error === "string" ? answer.body.error : null;
  const said =
    typeof answer.body?.detail === "string" && answer.body.detail !== "" ? answer.body.detail : null;

  if (answer.status === null) {
    return {
      kind: "unreachable",
      detail:
        "The console could not reach the service. Nothing has been enrolled, and no secret has been issued.",
    };
  }

  if (answer.status === 401) {
    // The distinction this whole file is arranged around.
    return error === "second_factor_wrong"
      ? {
          kind: "code_refused",
          // Verbatim, and it is the server that owns the wording for the reason
          // given in the header: its sentence covers a wrong code and a spent one
          // without saying which, and says what to do about either.
          detail: said ?? "That code did not confirm this enrolment. Wait for the next one and try again.",
        }
      : {
          kind: "session_ended",
          detail: said ?? "That sign-in is no longer live. Sign in again.",
        };
  }

  if (answer.status === 403) {
    return {
      kind: "already_enrolled",
      detail:
        said ??
        "This account already has a second factor in force. To set up a new one, ask Nova to reset it.",
    };
  }

  if (answer.status === 404) {
    return error === "no_enrolment_in_progress"
      ? { kind: "nothing_to_confirm", detail: said ?? "There is no enrolment waiting to be confirmed. Begin again." }
      : {
          kind: "not_offered",
          // Fastify's own 404 body, which carries a `message` rather than our
          // `detail`, so there is usually nothing to quote. The sentence says the
          // shape of the problem without guessing at the cause, because from a
          // browser a missing route and a mistyped base URL look the same.
          detail:
            "This service does not offer second-factor enrolment. That is how it is configured rather than " +
            "anything about this account: enrolment needs a sealing key for the secret, and an instance " +
            "without one has no enrolment routes at all. Nova has to configure it.",
        };
  }

  if (answer.status === 501) {
    return {
      kind: "not_offered",
      detail:
        said ??
        "This service cannot complete an enrolment. That is a key on the server rather than anything about " +
        "the code you typed.",
    };
  }

  if (answer.status === 400) {
    return {
      kind: "malformed",
      detail: said ?? "The console sent that in a way the server did not accept.",
    };
  }

  return {
    kind: "unavailable",
    detail: said ?? `The service answered ${answer.status}, which this console has no sentence for.`,
  };
}

/**
 * The enrolment body, or null if any part a person depends on is missing.
 *
 * Every field is checked rather than cast, because this is the one response in
 * XP1 carrying a shared secret and the failure mode of trusting it is silent: a
 * symbol that scans, an app that produces codes, and a refusal at the next
 * sign-in with nothing to point at.
 */
function asEnrolment(body: unknown): Enrolment | null {
  if (typeof body !== "object" || body === null) return null;
  const b = body as Record<string, unknown>;

  const factorId = text(b["factor_id"]);
  const secret = text(b["secret"]);
  const otpauthUri = text(b["otpauth_uri"]);
  const expiresAt = text(b["expires_at"]);
  const algorithm = text(b["algorithm"]);
  const digits = whole(b["digits"]);
  const periodSeconds = whole(b["period_seconds"]);

  if (
    factorId === null ||
    secret === null ||
    otpauthUri === null ||
    expiresAt === null ||
    algorithm === null ||
    digits === null ||
    periodSeconds === null
  ) {
    return null;
  }

  // A provisioning URI that is not one is refused here rather than encoded. The
  // check is on the scheme alone: what follows it is the server's business, and a
  // console that validated the query string would be a second opinion about the
  // format that the authenticator app is the real judge of.
  if (!otpauthUri.startsWith("otpauth://")) return null;

  return {
    factorId,
    secret,
    otpauthUri,
    expiresAt,
    algorithm,
    digits,
    periodSeconds,
    detail: text(b["detail"]) ?? "Add this to an authenticator app, then confirm it with a code from the app.",
  };
}

function asConfirmation(body: unknown): Confirmation | null {
  if (typeof body !== "object" || body === null) return null;
  const b = body as Record<string, unknown>;

  const factorId = text(b["factor_id"]);
  const confirmedAt = text(b["confirmed_at"]);
  if (factorId === null || confirmedAt === null) return null;

  return {
    factorId,
    confirmedAt,
    detail:
      text(b["detail"]) ??
      "That factor is in force. The sign-in you are holding was opened before it existed, so sign in again.",
  };
}

const text = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);

const whole = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value > 0 ? value : null;
